import {
    useCallback,
    useEffect,
    useEffectEvent,
    useMemo,
    useState
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStableQuery } from '../../db/useStableQuery';
import { useForm, useWatch, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { v4 as uuid } from 'uuid';
import { useRxCollection, useLiveRxQuery } from 'rxdb/plugins/react';
import {
    findingProfileInputSchema,
    isRecordNumberAllowed,
    isStructureFinding,
    PROFILE_FORM_OPTIONS,
    PROFILE_PART_OPTIONS,
    type FindingDocType,
    type FindingProfileDocType,
    type FindingPlanumDocType,
    type ParcelDocType
} from '@ast-data/shared';
import { useValidationMessage } from '../../i18n/validationMessages';
import { useSavedToast } from '../../components/toastContext';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { useFamilyQuotas } from '../projects/useFamilyQuotas';
import { useCurrentPerson, usePersonUserId } from '../users/personUserId';
import { useSelfPrefill } from '../users/useSelfPrefill';
import { parcelPickerOptions } from './parcelPickerLabel';
import {
    isSelfReference,
    siblingStrataOptions,
    syncInverseStratigraphy,
    syncSymmetricRefs,
    RECOMMENDED_PROFILE_FIELDS,
    describedSlotIssue,
    siblingParts,
    renamedPartSiblings,
    MEASURE_PROFILE_FIELDS,
    HEIGHT_FIELDS,
    STRATIGRAPHY_FIELDS
} from './descriptionValidation';
import {
    asDescriptionBinding,
    DimensionsSection,
    IdentitySection,
    LocationSection,
    MediaSectionBox,
    SoilSection,
    StratigraphySection
} from './descriptionSections';
import { useFindingBasePath, useFindingEditPath } from './findingRefLabels';
import { useStrataRouteMap } from './strataRefRoutes';
import DescriptionPartBar, {
    type DescriptionPartEntry
} from './DescriptionPartBar';
import {
    toCourseRows,
    cleanCourses,
    coursesKey,
    type CourseRow
} from './courseRows';
import {
    toColorRows,
    cleanColors,
    colorsKey,
    type ColorRow
} from './colorRows';
import {
    toInclusionRows,
    copiedInclusionRows,
    cleanInclusions,
    inclusionsKey,
    type InclusionRow
} from './inclusionRows';
import {
    toStratumRows,
    cleanStrata,
    strataKey,
    type StratumRow
} from './stratumRows';
import { mergeSketchStrata, type SketchStrata } from './sketchStrata';
import { mergeSketchInclusions } from './sketchInclusions';
import { type FindingRefs, emptyFindingRefs } from './findingRefSets';
import FormRow from '../../components/FormRow';
import FormDialogAppBar from '../../components/FormDialogAppBar';
import RecordFormDialog from '../../components/RecordFormDialog';
import DescriptionReferencesSection from './DescriptionReferencesSection';
import FormSection from '../../components/FormSection';
import { SectionNavMenuButton } from '../../components/formSectionNav';
import { type FormSection as FormSectionNavEntry } from '../../components/formSectionNavContext';
import {
    bindFormFields,
    type FormFieldBinding,
    requiredInForm
} from '../../components/formFields';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { useRecordTranslations } from '../translation/recordTranslations';
import { useUnsavedCloseGuard } from '../../components/useUnsavedCloseGuard';
import { useGuardedNavigate } from '../../components/useGuardedNavigate';
import { useFindingRefSupport } from './useFindingRefSupport';
import { useFindingSequence } from './useFindingSequence';
import {
    useForwardBackState,
    useDialogBackCrumb
} from '../../components/dialogBackTrail';
import { useFocusFieldOnOpen } from '../../components/cellFocus';

import { compareText } from '../../utils/compare';

// Tree-grid columns whose `field` differs from the Profil form input to focus.
const PROFILE_CELL_FOCUS_ALIASES: Record<string, string> = {
    date: 'recordedAt',
    parcelLabel: 'parcelId'
};

/**
 * The Bodenmerkmale, which take the PLANUM's labels — see the form binding's
 * `labelId` below.
 *
 * Not `profile.field.*`: those strings do not exist, and a Profil showing them
 * is the exception (a Befund that never appeared in plan), not a second
 * vocabulary.
 */
const SOIL_LABEL_FIELDS = new Set<string>([
    'soilType',
    'boundaryClarity',
    'boundaryBasis',
    'consistency',
    'contour',
    'humusContent'
]);

/**
 * Ordered sections for the in-form nav; must mirror the section boxes below.
 * The Media box always renders; the Verweise box only when editing (see
 * buildFormSections).
 */
const BASE_FORM_SECTIONS: FormSectionNavEntry[] = [
    { id: 'identity', titleId: 'profile.section.identity' },
    { id: 'location', titleId: 'profile.section.location' },
    { id: 'dimensions', titleId: 'profile.section.dimensions' },
    { id: 'stratigraphy', titleId: 'profile.section.stratigraphy' },
    { id: 'media', titleId: 'media.title' }
];

function buildFormSections(
    isEdit: boolean,
    withSoil: boolean
): FormSectionNavEntry[] {
    const sections = [...BASE_FORM_SECTIONS];
    if (withSoil) {
        // Between Maße and Stratigraphie, where the Planum form has it too.
        sections.splice(3, 0, {
            id: 'soil',
            titleId: 'profile.section.soil'
        });
    }
    if (!isEdit) {
        return sections;
    }
    return [
        ...sections,
        { id: 'references', titleId: 'descriptionRefs.title' }
    ];
}

type FormValues = {
    findingId: string;
    parcelId: string;
    profileNumber: string;
    partLabel: string;
    trench: string;
    area: string;
    sondage: string;
    editor: string;
    width: string;
    depth: string;
    heightTop: string;
    heightBottom: string;
    form: string;
    orientation: string;
    excavationMethod: string;
    soilType: string;
    consistency: string;
    boundaryClarity: string;
    boundaryBasis: string;
    contour: string;
    humusContent: string;
    overliesOrCuts: string;
    underliesOrIsCut: string;
    contemporaryWith: string;
    fills: string;
    filledBy: string;
    description: string;
    interpretation: string;
    recordedAt: string;
    phaseNumber: string;
};

function emptyValues(findingId: string): FormValues {
    return {
        findingId,
        parcelId: '',
        profileNumber: '',
        partLabel: '',
        trench: '',
        area: '',
        sondage: '',
        editor: '',
        width: '',
        depth: '',
        heightTop: '',
        heightBottom: '',
        form: '',
        orientation: '',
        excavationMethod: '',
        soilType: '',
        consistency: '',
        boundaryClarity: '',
        boundaryBasis: '',
        contour: '',
        humusContent: '',
        overliesOrCuts: '',
        underliesOrIsCut: '',
        contemporaryWith: '',
        fills: '',
        filledBy: '',
        description: '',
        interpretation: '',
        recordedAt: new Date().toISOString(),
        phaseNumber: ''
    };
}

function toValues(profile: FindingProfileDocType): FormValues {
    return {
        findingId: profile.findingId,
        parcelId: profile.parcelId,
        profileNumber: profile.profileNumber,
        partLabel: profile.partLabel,
        trench: profile.trench,
        area: profile.area,
        sondage: profile.sondage,
        editor: profile.editor,
        width: profile.width,
        depth: profile.depth,
        heightTop: profile.heightTop,
        heightBottom: profile.heightBottom,
        form: profile.form,
        orientation: profile.orientation,
        excavationMethod: profile.excavationMethod,
        soilType: profile.soilType,
        consistency: profile.consistency,
        boundaryClarity: profile.boundaryClarity,
        boundaryBasis: profile.boundaryBasis,
        contour: profile.contour,
        humusContent: profile.humusContent,
        overliesOrCuts: profile.overliesOrCuts,
        underliesOrIsCut: profile.underliesOrIsCut,
        contemporaryWith: profile.contemporaryWith,
        fills: profile.fills,
        filledBy: profile.filledBy,
        description: profile.description,
        interpretation: profile.interpretation,
        recordedAt: profile.recordedAt,
        phaseNumber: profile.phaseNumber
    };
}

/**
 * A new Teil seeded off the one it was added from — the Profil's counterpart to
 * the Planum form's. The Lage, the Form/Ausrichtung and the Bodenmerkmale belong
 * to the Profil and carry over; the measures, the text, the Schichten, the
 * stratigraphy, the Aufnahmedatum and the Verfasser are what the Teil is for and
 * start empty.
 */
function seededValues(
    source: FindingProfileDocType,
    partLabel: string
): FormValues {
    return {
        ...emptyValues(source.findingId),
        parcelId: source.parcelId,
        profileNumber: source.profileNumber,
        partLabel,
        trench: source.trench,
        area: source.area,
        sondage: source.sondage,
        form: source.form,
        orientation: source.orientation,
        excavationMethod: source.excavationMethod,
        soilType: source.soilType,
        consistency: source.consistency,
        boundaryClarity: source.boundaryClarity,
        boundaryBasis: source.boundaryBasis,
        contour: source.contour,
        humusContent: source.humusContent,
        phaseNumber: source.phaseNumber
    };
}

function distinct(values: string[]): string[] {
    return [...new Set(values.filter(Boolean))].sort(
        (firstValue, secondValue) => compareText(firstValue, secondValue)
    );
}

interface FindingProfileFormDialogProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    findingId: string;
    profile?: FindingProfileDocType | null;
    /**
     * The Teil a new description was added from ("Teil hinzufügen" → `?seedFrom`).
     * Only read while creating; see {@link seededValues} for what it carries.
     */
    seedFrom?: FindingProfileDocType | null;
    /** Grid column the user double-clicked, to focus the matching input on open. */
    focusField?: string;
}

/**
 * The Profil editor — a fullscreen dialog (better on mobile) whose sections are
 * Paper "boxes" like the Metadaten page, with a sticky Save/Cancel footer.
 * Mirrors FindingFormDialog; this form is long enough that the boxes carry most
 * of the benefit.
 */
/**
 * The person the record is written up by is required in the form — see
 * requiredInForm.
 */
const PROFILE_FORM_SCHEMA = requiredInForm(findingProfileInputSchema, [
    'editor'
]);

export default function FindingProfileFormDialog({
    open,
    onClose,
    projectId,
    findingId,
    profile,
    seedFrom,
    focusField
}: FindingProfileFormDialogProps) {
    const intl = useIntl();
    const collection =
        useRxCollection<FindingProfileDocType>('finding_profiles');
    const validationMessage = useValidationMessage();
    const notifySaved = useSavedToast();
    const isEdit = !!profile;
    // A reader opens the record to look at it: the dialog shows it as a
    // sheet and has nothing to save (see RecordFormDialog).
    const readOnly = !useAbility().can(
        isEdit ? 'update' : 'create',
        scopedSubject('FindingProfile', { projectId })
    );

    const ability = useAbility();
    const canEditMedia = ability.can(
        'update',
        scopedSubject('FindingProfile', { projectId })
    );

    // Parcels + Befund numbers (picker + stratigraphy cross-refs) span the whole
    // Teilmaßnahme family — linked projects share their Befunde.
    const familyIds = useFamilyProjectIds(projectId);

    // Profil rows span the family: the positional stratigraphy pickers list
    // sibling descriptions on the same Fläche/Profil, and a shared Befund's
    // rows may sit in any family project.
    const familyProfilesQuery = useStableQuery<FindingProfileDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: familyProfilesDocs } =
        useLiveRxQuery<FindingProfileDocType>({
            collection: 'finding_profiles',
            query: familyProfilesQuery
        });
    const familyProfiles = useMemo(
        () =>
            familyProfilesDocs.map(
                (doc) => doc.toMutableJSON() as FindingProfileDocType
            ),
        [familyProfilesDocs]
    );
    // Planum rows too: "Teil von / identisch mit" offers (and mirrors onto)
    // descriptions of both kinds across the family.
    const familyPlanumsQuery = useStableQuery<FindingPlanumDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: familyPlanumsDocs } = useLiveRxQuery<FindingPlanumDocType>(
        {
            collection: 'finding_planums',
            query: familyPlanumsQuery
        }
    );
    const familyPlanums = useMemo(
        () =>
            familyPlanumsDocs.map(
                (doc) => doc.toMutableJSON() as FindingPlanumDocType
            ),
        [familyPlanumsDocs]
    );
    const planumCollection =
        useRxCollection<FindingPlanumDocType>('finding_planums');
    // Backtrack trench/editor suggestions from THIS project's rows only —
    // spatial slots and crews differ between phases.
    const profiles = useMemo(
        () =>
            familyProfiles.filter((profile) => profile.projectId === projectId),
        [familyProfiles, projectId]
    );
    const planums = useMemo(
        () => familyPlanums.filter((planum) => planum.projectId === projectId),
        [familyPlanums, projectId]
    );

    const parcelsQuery = useStableQuery<ParcelDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: parcelsDocs } = useLiveRxQuery<ParcelDocType>({
        collection: 'parcels',
        query: parcelsQuery
    });
    const parcels = useMemo(
        () => parcelsDocs.map((doc) => doc.toMutableJSON() as ParcelDocType),
        [parcelsDocs]
    );
    const findingsQuery = useStableQuery<FindingDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: findingsDocs } = useLiveRxQuery<FindingDocType>({
        collection: 'findings',
        query: findingsQuery
    });
    const findings = useMemo(
        () => findingsDocs.map((doc) => doc.toMutableJSON() as FindingDocType),
        [findingsDocs]
    );

    const currentUserName = useCurrentPerson()?.name ?? '';
    // The links this record already carries, name by name: a name nobody
    // touched keeps its account even where the rule can no longer place it
    // (see usePersonUserId).
    const keptLinks = useMemo(
        () => (profile ? [{ name: profile.editor, id: profile.editorId }] : []),
        [profile]
    );
    const { resolve } = usePersonUserId(keptLinks);

    const suggestions = useMemo(
        () => ({
            trench: distinct([
                ...profiles.map((profile) => profile.trench),
                ...planums.map((planum) => planum.trench)
            ]),
            area: distinct([
                ...profiles.map((profile) => profile.area),
                ...planums.map((planum) => planum.area)
            ]),
            sondage: distinct([
                ...profiles.map((profile) => profile.sondage),
                ...planums.map((planum) => planum.sondage)
            ])
        }),
        [profiles, planums]
    );

    const ownFinding = useMemo(
        () => findings.find((finding) => finding.id === findingId) ?? null,
        [findings, findingId]
    );
    const ownFindingNumber = ownFinding?.findingNumber ?? '';

    const befundEditPath = useFindingEditPath();

    const parcelOptions = useMemo(
        () => parcelPickerOptions(parcels),
        [parcels]
    );

    // The Kontingente in force for the Profilnummer — family-scoped like the
    // uniqueness rule. Empty means no allocation yet, so the number is free text.
    const { profileQuotas } = useFamilyQuotas(projectId);

    const {
        register,
        handleSubmit,
        reset,
        getValues,
        setValue,
        setError,
        setFocus,
        control,
        formState: { errors, isSubmitting, isDirty }
    } = useForm<FormValues>({
        resolver: zodResolver(
            PROFILE_FORM_SCHEMA
        ) as unknown as Resolver<FormValues>,
        defaultValues: emptyValues(findingId)
    });

    const fieldBinding: FormFieldBinding<FormValues> = {
        intl,
        readOnly,
        register,
        control,
        errors,
        validationMessage,
        // The Bodenmerkmale are labelled from the PLANUM catalog — one soil
        // vocabulary for both descriptions, so the two can't drift apart.
        labelId: (name) =>
            SOIL_LABEL_FIELDS.has(name)
                ? `planum.field.${name}`
                : `profile.field.${name}`,
        recommended: (name) => RECOMMENDED_PROFILE_FIELDS.has(name),
        unitId: (name) => {
            if (HEIGHT_FIELDS.has(name)) {
                return 'common.metres';
            }
            if (MEASURE_PROFILE_FIELDS.has(name)) {
                return 'common.centimetres';
            }
            return undefined;
        }
    };
    const { text, suggest } = bindFormFields(fieldBinding);
    const sectionBinding = asDescriptionBinding(fieldBinding);

    // "Teil von / identisch mit" references specific descriptions (Planum/Profil),
    // managed as ref arrays like the photo/OD Befund pickers.
    const linkedIdenticalRefs = useMemo<FindingRefs>(
        () => ({
            planumIds: profile?.identicalWithPlanumIds ?? [],
            profileIds: profile?.identicalWithProfileIds ?? [],
            findingIds: []
        }),
        [profile]
    );
    const [identicalRefs, setIdenticalRefs] =
        useState<FindingRefs>(emptyFindingRefs());
    const linkedPartOfRefs = useMemo<FindingRefs>(
        () => ({
            planumIds: profile?.partOfPlanumIds ?? [],
            profileIds: profile?.partOfProfileIds ?? [],
            findingIds: []
        }),
        [profile]
    );
    const [partOfRefs, setPartOfRefs] =
        useState<FindingRefs>(emptyFindingRefs());
    const linkedInProfileWithRefs = useMemo<FindingRefs>(
        () => ({
            planumIds: profile?.inProfileWithPlanumIds ?? [],
            profileIds: profile?.inProfileWithProfileIds ?? [],
            findingIds: []
        }),
        [profile]
    );
    const [inProfileWithRefs, setInProfileWithRefs] =
        useState<FindingRefs>(emptyFindingRefs());

    // Bodenmerkmale: only meaningful for a Befund that never showed in plan, so
    // the section below is gated on the Befund's flag — but the STATE is always
    // here, so a record that already holds readings keeps them either way.
    const existingColors = useMemo<ColorRow[]>(
        () => toColorRows(profile?.colors ?? seedFrom?.colors),
        [profile, seedFrom]
    );
    const [colors, setColors] = useState<ColorRow[]>([]);
    const existingInclusions = useMemo<InclusionRow[]>(
        () =>
            profile
                ? toInclusionRows(profile.inclusions)
                : copiedInclusionRows(seedFrom?.inclusions),
        [profile, seedFrom]
    );
    const [inclusions, setInclusions] = useState<InclusionRow[]>([]);

    // The Schichten and their own Breite/Tiefe. Local state like the two lists
    // above — and, unlike them, also written to from OUTSIDE the form: a Schicht
    // drawn on a sketch seeds its row here (see sketchStrata).
    const existingStrata = useMemo<StratumRow[]>(
        () => toStratumRows(profile?.strata),
        [profile]
    );
    const [strata, setStrata] = useState<StratumRow[]>([]);

    // The Lagen — what a baulicher Bestand is measured by instead of the flat
    // fields.
    const existingCourses = useMemo<CourseRow[]>(
        () => toCourseRows(profile?.courses),
        [profile]
    );
    const [courses, setCourses] = useState<CourseRow[]>([]);
    const stratumWord = intl.formatMessage({ id: 'sketch.stratum' });
    const seedStrata = useCallback(
        (sketches: SketchStrata[]) =>
            setStrata((rows) => mergeSketchStrata(rows, sketches, stratumWord)),
        // `setStrata` is listed although a setter is stable: the React Compiler
        // infers it as a dependency and refuses to keep a memo whose written
        // deps it cannot match (see react-hooks/preserve-manual-memoization).
        [stratumWord, setStrata]
    );
    // A pen drawn on a sketch seeds its Einschluss (see sketchInclusions).
    const seedInclusions = useCallback(
        (penIds: string[]) =>
            setInclusions((rows) => mergeSketchInclusions(rows, penIds)),
        [setInclusions]
    );

    /**
     * Whether this Profil carries the Bodenmerkmale. Normally the Planum does —
     * but a Befund flagged "nur im Profil sichtbar" has none, so they are
     * recorded here. Shown for a record that already HOLDS readings too, so an
     * unticked box never hides data that was entered (or imported) earlier.
     * Whether this description belongs to a „baulicher Bestand" — the one thing
     * that changes what the form asks for. It is the BEFUND's type, set on the
     * Befund form and never here (see FINDING_TYPE_OPTIONS).
     */
    const isStructure = isStructureFinding(ownFinding?.findingType);
    const showSoil =
        !isStructure &&
        (Boolean(ownFinding?.onlyVisibleInProfile) ||
            colors.length > 0 ||
            inclusions.length > 0 ||
            Boolean(profile?.soilType || profile?.consistency));

    // The positional relations (liegt über/schneidet, liegt unter/wird
    // geschnitten, zeitgleich mit) only make sense against descriptions on the
    // SAME Fläche and Profil — the options follow the form's current values.
    const [
        watchedArea,
        watchedProfileNumber,
        watchedPartLabel,
        watchedFindingId
    ] = useWatch({
        control,
        name: ['area', 'profileNumber', 'partLabel', 'findingId']
    });

    // Every Profil description of the family, reduced to the slot it occupies
    // (Befund + Nummer + Teil) and carrying the route to its own editor — the
    // Planum form's counterpart, and the input of both the Teil bar and the
    // slot rule.
    const findingBase = useFindingBasePath();
    const slotRows = useMemo(
        () =>
            familyProfiles.map((row) => ({
                id: row.id,
                findingId: row.findingId,
                number: row.profileNumber,
                partLabel: row.partLabel,
                to: `${findingBase(row.projectId, row.findingId)}/profiles/${row.id}/edit`
            })),
        [familyProfiles, findingBase]
    );
    const partEntries = useMemo<DescriptionPartEntry[]>(
        () =>
            siblingParts(
                { findingId: watchedFindingId, number: watchedProfileNumber },
                slotRows
            ),
        [slotRows, watchedFindingId, watchedProfileNumber]
    );
    // The marks still FREE for this number: a Teil may exist only once (see
    // describedSlotIssue), so offering a taken one would only propose the error.
    // The open description's own mark stays, or editing it would look invalid.
    const freePartOptions = useMemo(
        () =>
            PROFILE_PART_OPTIONS.filter(
                (option) =>
                    !partEntries.some(
                        (entry) =>
                            entry.id !== (profile?.id ?? '') &&
                            entry.partLabel.trim().toLowerCase() ===
                                option.toLowerCase()
                    )
            ),
        [partEntries, profile]
    );
    // The family's Profil rows in the shape both the options and their routes
    // are read from: which slot each one occupies, and how to get to it.
    const strataSiblings = useMemo(
        () =>
            familyProfiles.map((profile) => ({
                id: profile.id,
                projectId: profile.projectId,
                findingId: profile.findingId,
                area: profile.area,
                number: profile.profileNumber
            })),
        [familyProfiles]
    );
    const positionalOptions = useMemo(
        () =>
            siblingStrataOptions({
                siblings: strataSiblings,
                findings,
                ownFindingId: findingId,
                area: watchedArea ?? '',
                number: watchedProfileNumber ?? '',
                kind: 'Profil'
            }),
        [strataSiblings, findings, findingId, watchedArea, watchedProfileNumber]
    );
    // Only the Befundnummer is stored, so a chip's link is resolved back to the
    // description it was picked from.
    const positionalRoutes = useStrataRouteMap({
        siblings: strataSiblings,
        findings,
        area: watchedArea ?? '',
        number: watchedProfileNumber ?? '',
        kind: 'profile'
    });

    // The account names can arrive after the dialog opened, and the reset
    // below then had nothing to prefill with (see useSelfPrefill).
    useSelfPrefill(open && !profile, currentUserName, (self) => {
        if (getValues('editor').trim() === '') {
            setValue('editor', self);
        }
    });

    // An effect EVENT: every prefill source is read at its CURRENT value,
    // while the reset itself runs only when the dialog (re)opens for a
    // record — a background users-sync must not wipe what was typed.
    const resetForOpening = useEffectEvent(() => {
        const values = profile
            ? toValues(profile)
            : seedFrom
              ? // The Teil mark stays EMPTY and takes the focus (below): a
                /**
                 * proposed "A"/"1" is a guess at what the recorder called
                 * this piece in the field, and one they would have had to
                 * notice to correct. The picker still offers the convention.
                 */
                seededValues(seedFrom, '')
              : emptyValues(findingId);
        reset({
            ...values,
            /**
             * Default the Verfasser to whoever is recording — on a new
             * Profil, and on an older one that never got an editor. A name
             * already on the record is left alone.
             */
            editor: values.editor || currentUserName
        });
        setIdenticalRefs(linkedIdenticalRefs);
        setPartOfRefs(linkedPartOfRefs);
        setInProfileWithRefs(linkedInProfileWithRefs);
        setColors(existingColors);
        setInclusions(existingInclusions);
        setStrata(existingStrata);
        setCourses(existingCourses);
    });
    useEffect(() => {
        if (open) {
            // react-hook-form's `reset` publishes to its own subscribers, so
            // it cannot be moved into the render pass the way plain state can.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            resetForOpening();
        }
        // `seedFrom` belongs in here: the seed record resolves a render AFTER
        // the dialog mounts (it is an RxDB lookup), so without it "Teil
        // hinzufuegen" opened an EMPTY Profil.
    }, [open, profile, seedFrom, findingId]);

    /**
     * Focus the input for the grid cell that was double-clicked to open this.
     * A seeded Teil opens with the cursor in the Teil field — it is the one
     * thing the seed cannot know and the only thing that tells this Profil apart
     * from the one it was seeded from.
     */
    useFocusFieldOnOpen(
        setFocus,
        open,
        focusField ?? (!profile && seedFrom ? 'partLabel' : undefined),
        PROFILE_CELL_FOCUS_ALIASES
    );

    // The "identisch mit" refs are local state, so fold them into the dirty check.
    const identicalDirty = useMemo(() => {
        const key = (refs: FindingRefs) =>
            [...refs.planumIds].sort().join(',') +
            '|' +
            [...refs.profileIds].sort().join(',');
        return (
            key(identicalRefs) !== key(linkedIdenticalRefs) ||
            key(partOfRefs) !== key(linkedPartOfRefs) ||
            key(inProfileWithRefs) !== key(linkedInProfileWithRefs)
        );
    }, [
        identicalRefs,
        linkedIdenticalRefs,
        partOfRefs,
        linkedPartOfRefs,
        inProfileWithRefs,
        linkedInProfileWithRefs
    ]);

    const soilListsDirty = useMemo(
        () =>
            colorsKey(cleanColors(colors)) !==
                colorsKey(profile?.colors ?? []) ||
            inclusionsKey(cleanInclusions(inclusions)) !==
                inclusionsKey(profile?.inclusions ?? []),
        [colors, inclusions, profile]
    );

    const coursesDirty = useMemo(
        () =>
            coursesKey(cleanCourses(courses)) !==
            coursesKey(profile?.courses ?? []),
        [courses, profile]
    );

    // A sketch can add Schichten while the form is open, so this is what makes a
    // seeded row worth saving.
    const strataDirty = useMemo(
        () =>
            strataKey(cleanStrata(strata)) !== strataKey(profile?.strata ?? []),
        [strata, profile]
    );

    // Esc, browser back and the close/cancel buttons ask before dropping
    // unsaved edits (save / discard / keep editing).
    const translations = useRecordTranslations({
        ownerType: 'findingProfile',
        ownerId: profile?.id,
        projectId,
        readOnly
    });
    const closeGuard = useUnsavedCloseGuard({
        open,
        isDirty:
            isDirty ||
            identicalDirty ||
            soilListsDirty ||
            strataDirty ||
            coursesDirty ||
            translations.isDirty,
        onClose,
        onSave: () => handleSubmit(onSubmit)()
    });
    /**
     * Leaving for a referenced record honors unsaved edits (the close guard only
     * covers back/close/reload, not this in-app push). `forwardState` tags each
     * onward jump with this Profil so the target can offer a "back" here.
     * What this description is CALLED — "Profil 1", or "Profil 1, Teil A" once it is
     * one piece of a divided one. The number alone would name two Teile alike,
     * in the dialog's own header and in the "zurück zu …" crumb that quotes it.
     */
    const selfLabel = profile?.partLabel.trim()
        ? intl.formatMessage(
              { id: 'profile.labelPart' },
              {
                  number: profile.profileNumber || '?',
                  part: profile.partLabel.trim()
              }
          )
        : intl.formatMessage(
              { id: 'profile.label' },
              { number: profile?.profileNumber || '?' }
          );
    const guardedNav = useGuardedNavigate({
        isDirty:
            isDirty ||
            identicalDirty ||
            soilListsDirty ||
            strataDirty ||
            coursesDirty ||
            translations.isDirty,
        submit: () => handleSubmit(onSubmit)(),
        close: () => closeGuard.close(),
        forwardState: useForwardBackState(selfLabel)
    });
    const backCrumb = useDialogBackCrumb(closeGuard.requestBack);
    // Step through the Befunde with their Plana and Profile in between —
    // through the nav guard, like any other link out of an open form (see
    // useFindingSequence).
    const siblings = useFindingSequence({
        projectId,
        currentId: profile?.id ?? '',
        requestNavigate: guardedNav.requestNavigate
    });

    // The Befunde the running description names, marked and linked — the same
    // editor the Tagebuch's prose fields carry. Defined AFTER `guardedNav`: a
    // reference is a link out of an open editor, so it has to ask before
    // leaving, exactly as the Verweise chips do.
    const findingRefs = useFindingRefSupport(
        projectId,
        guardedNav.requestNavigate
    );

    async function onSubmit(data: FormValues) {
        if (readOnly) {
            return;
        }
        const profileNumber = data.profileNumber.trim();
        // Once the family has a Kontingent, the number must be an integer inside
        // one of its ranges. No Kontingent = unrestricted (pre-quota behaviour).
        if (!isRecordNumberAllowed(profileQuotas, profileNumber)) {
            setError(
                'profileNumber',
                {
                    type: 'validate',
                    message: 'outsideQuota'
                },
                { shouldFocus: true }
            );
            return;
        }
        /**
         * Profilnummern are unique only WITHIN their Befund — two Befunde may
         * both have a "Profil 1". The Kontingent says which numbers are allowed,
         * it does not hand each number out once. Checked against the Befund the
         * form currently targets (the picker can move a Profil to another one),
         * across the family — a shared Befund's rows may sit in any project.
         *
         * And a Profil is EITHER described once or split into Teile that are
         * each described once, never both — which is the rest of what
         * describedSlotIssue decides.
         */
        const partLabel = data.partLabel.trim();
        const slotIssue = describedSlotIssue(
            {
                id: profile?.id ?? '',
                findingId: data.findingId,
                number: profileNumber,
                partLabel
            },
            slotRows
        );
        if (slotIssue) {
            setError(
                slotIssue.target === 'part' ? 'partLabel' : 'profileNumber',
                { type: 'validate', message: slotIssue.code },
                { shouldFocus: true }
            );
            return;
        }
        // The stratigraphy fields cross-reference OTHER Befunde — this Profil's
        // own Befund may not appear among them.
        const selfReferencing = [...STRATIGRAPHY_FIELDS].filter((name) =>
            isSelfReference(
                data[name as keyof FormValues] ?? '',
                ownFindingNumber
            )
        );
        if (selfReferencing.length > 0) {
            selfReferencing.forEach((name, index) => {
                setError(
                    name as keyof FormValues,
                    { type: 'validate', message: 'selfReference' },
                    { shouldFocus: index === 0 }
                );
            });
            return;
        }

        const now = new Date().toISOString();
        // A stable own id (an insert's uuid up front) so the identity sync can
        // mirror this description's ref onto the ones it points at.
        const ownId = isEdit ? profile!.id : uuid();
        const content = {
            ...data,
            profileNumber,
            partLabel,
            editorId: resolve(data.editor),
            identicalWithPlanumIds: identicalRefs.planumIds,
            identicalWithProfileIds: identicalRefs.profileIds,
            partOfPlanumIds: partOfRefs.planumIds,
            partOfProfileIds: partOfRefs.profileIds,
            inProfileWithPlanumIds: inProfileWithRefs.planumIds,
            inProfileWithProfileIds: inProfileWithRefs.profileIds,
            // The six text Bodenmerkmale ride along in `data`; the two lists
            // are local state like the Planum's.
            colors: cleanColors(colors),
            inclusions: cleanInclusions(inclusions),
            strata: cleanStrata(strata),
            courses: cleanCourses(courses)
        };
        if (isEdit && collection) {
            const doc = await collection.findOne(ownId).exec();
            if (doc) {
                await doc.incrementalPatch({ ...content, updatedAt: now });
            }
        } else if (collection) {
            await collection.insert({
                id: ownId,
                projectId,
                ...content,
                createdBy: '',
                updatedBy: '',
                updatedAt: now,
                createdAt: now
            });
        }
        await translations.commit(ownId);
        // A Profil recorded in Teile carries ONE number: correcting it here
        // corrects it on the other Teile too, or the description falls apart
        // into pieces of two different numbers (see renamedPartSiblings).
        if (collection && isEdit) {
            for (const siblingId of renamedPartSiblings({
                self: {
                    id: ownId,
                    findingId: data.findingId,
                    number: profileNumber,
                    partLabel
                },
                previous: {
                    findingId: profile!.findingId,
                    number: profile!.profileNumber
                },
                siblings: slotRows
            })) {
                const sibling = await collection.findOne(siblingId).exec();
                if (sibling) {
                    await sibling.incrementalPatch({
                        profileNumber,
                        updatedAt: now
                    });
                }
            }
        }
        // Mirror the positional relations onto the referenced Befunde ("A über
        // B" writes "B unter A" on B's description of the same Fläche/Profil).
        if (collection) {
            await syncInverseStratigraphy({
                siblings: familyProfiles.map((profile) => ({
                    id: profile.id,
                    findingId: profile.findingId,
                    area: profile.area,
                    slotNumber: profile.profileNumber,
                    overliesOrCuts: profile.overliesOrCuts,
                    underliesOrIsCut: profile.underliesOrIsCut,
                    contemporaryWith: profile.contemporaryWith,
                    fills: profile.fills,
                    filledBy: profile.filledBy
                })),
                findings,
                ownFindingId: data.findingId,
                before: {
                    area: profile?.area ?? '',
                    slotNumber: profile?.profileNumber ?? '',
                    overliesOrCuts: profile?.overliesOrCuts ?? '',
                    underliesOrIsCut: profile?.underliesOrIsCut ?? '',
                    contemporaryWith: profile?.contemporaryWith ?? '',
                    fills: profile?.fills ?? '',
                    filledBy: profile?.filledBy ?? ''
                },
                after: {
                    area: data.area,
                    slotNumber: profileNumber,
                    overliesOrCuts: data.overliesOrCuts,
                    underliesOrIsCut: data.underliesOrIsCut,
                    contemporaryWith: data.contemporaryWith,
                    fills: data.fills,
                    filledBy: data.filledBy
                },
                applyPatch: async (id, patch) => {
                    const sibling = await collection.findOne(id).exec();
                    if (sibling) {
                        await sibling.incrementalPatch({
                            ...patch,
                            updatedAt: now
                        });
                    }
                }
            });
            // Both symmetric relations close over the GROUP they name: four
            // descriptions listed as identical are identical with each other,
            // so all four end up carrying the other three.
            for (const relation of [
                'identicalWith',
                'inProfileWith'
            ] as const) {
                await syncSymmetricRefs({
                    relation,
                    rows: [
                        ...familyPlanums.map((planum) => ({
                            id: planum.id,
                            kind: 'planum' as const,
                            identicalWithPlanumIds:
                                planum.identicalWithPlanumIds,
                            identicalWithProfileIds:
                                planum.identicalWithProfileIds,
                            inProfileWithPlanumIds:
                                planum.inProfileWithPlanumIds,
                            inProfileWithProfileIds:
                                planum.inProfileWithProfileIds
                        })),
                        ...familyProfiles.map((profile) => ({
                            id: profile.id,
                            kind: 'profile' as const,
                            identicalWithPlanumIds:
                                profile.identicalWithPlanumIds,
                            identicalWithProfileIds:
                                profile.identicalWithProfileIds,
                            inProfileWithPlanumIds:
                                profile.inProfileWithPlanumIds,
                            inProfileWithProfileIds:
                                profile.inProfileWithProfileIds
                        }))
                    ],
                    own: { id: ownId, kind: 'profile' },
                    before:
                        relation === 'identicalWith'
                            ? {
                                  planumIds:
                                      profile?.identicalWithPlanumIds ?? [],
                                  profileIds:
                                      profile?.identicalWithProfileIds ?? []
                              }
                            : {
                                  planumIds:
                                      profile?.inProfileWithPlanumIds ?? [],
                                  profileIds:
                                      profile?.inProfileWithProfileIds ?? []
                              },
                    after:
                        relation === 'identicalWith'
                            ? {
                                  planumIds: identicalRefs.planumIds,
                                  profileIds: identicalRefs.profileIds
                              }
                            : {
                                  planumIds: inProfileWithRefs.planumIds,
                                  profileIds: inProfileWithRefs.profileIds
                              },
                    applyPatch: async (kind, id, patch) => {
                        const target =
                            kind === 'profile' ? collection : planumCollection;
                        const sibling = await target?.findOne(id).exec();
                        if (sibling) {
                            await sibling.incrementalPatch({
                                ...patch,
                                updatedAt: now
                            });
                        }
                    }
                });
            }
        }
        notifySaved();
        // Go to a "save and go" reference target if one is pending, else close
        // (guard-aware: a routed dialog steps over the guard's history sentinel).
        guardedNav.finishSubmit();
    }

    return (
        <RecordFormDialog
            open={open}
            sections={buildFormSections(isEdit, showSoil)}
            onSubmit={handleSubmit(onSubmit)}
            closeGuard={closeGuard}
            navGuard={guardedNav}
            isSubmitting={isSubmitting}
            readOnly={readOnly}
            translations={translations}
            appBar={
                <FormDialogAppBar
                    projectId={projectId}
                    onClose={closeGuard.requestClose}
                    startSlot={<SectionNavMenuButton />}
                    backCrumb={backCrumb}
                    findingCrumb={
                        findingId
                            ? {
                                  number: ownFindingNumber,
                                  onOpen: () =>
                                      guardedNav.requestNavigate(
                                          befundEditPath(projectId, findingId)
                                      ),
                                  // Also a real link, so the Befund can be
                                  // opened in a new tab from here.
                                  to: befundEditPath(projectId, findingId)
                              }
                            : undefined
                    }
                    siblings={siblings}
                    title={
                        isEdit && profile?.profileNumber ? (
                            selfLabel
                        ) : (
                            <FormattedMessage
                                id={
                                    isEdit
                                        ? 'profile.form.editTitle'
                                        : 'profile.form.newTitle'
                                }
                            />
                        )
                    }
                />
            }
        >
            <IdentitySection
                binding={sectionBinding}
                titlePrefix="profile.section"
                numberField={text('profileNumber')}
                partField={suggest('partLabel', freePartOptions)}
                partBar={
                    <DescriptionPartBar
                        parts={partEntries}
                        currentId={profile?.id ?? null}
                        currentLabel={watchedPartLabel}
                        onOpen={guardedNav.requestNavigate}
                        onAdd={
                            profile && canEditMedia
                                ? () =>
                                      // Saves this Teil first — the sibling is
                                      // seeded from the STORED record, so an
                                      // unsaved mark would seed a clash.
                                      void guardedNav.saveAndNavigate(
                                          `${findingBase(
                                              profile.projectId,
                                              profile.findingId
                                          )}/profiles/new?seedFrom=${profile.id}`
                                      )
                                : undefined
                        }
                    />
                }
                findingRefs={findingRefs}
                findingType={ownFinding?.findingType}
            />
            <LocationSection
                binding={sectionBinding}
                titlePrefix="profile.section"
                parcelOptions={parcelOptions}
                suggestions={suggestions}
            />
            <DimensionsSection
                binding={sectionBinding}
                titlePrefix="profile.section"
                labelPrefix="profile.field"
                formOptions={PROFILE_FORM_OPTIONS}
                measures={
                    <FormRow>
                        {text('width')}
                        {text('depth')}
                    </FormRow>
                }
                strata={strata}
                onStrataChange={setStrata}
                strataLength={false}
                strataDepth
                courses={isStructure ? courses : undefined}
                onCoursesChange={isStructure ? setCourses : undefined}
            />
            {/* Only for the Befund that never showed in plan — see
                            showSoil. The Planum form's very editors. */}
            {showSoil && (
                <SoilSection
                    binding={sectionBinding}
                    titlePrefix="profile.section"
                    colors={colors}
                    onColorsChange={setColors}
                    inclusions={inclusions}
                    onInclusionsChange={setInclusions}
                />
            )}
            <StratigraphySection
                binding={sectionBinding}
                titlePrefix="profile.section"
                positionalOptions={positionalOptions}
                positionalRoutes={positionalRoutes}
                projectId={projectId}
                findingId={findingId}
                inProfileWith={inProfileWithRefs}
                onInProfileWithChange={setInProfileWithRefs}
                identical={identicalRefs}
                onIdenticalChange={setIdenticalRefs}
                partOf={partOfRefs}
                onPartOfChange={setPartOfRefs}
                onOpenRef={guardedNav.requestNavigate}
                showFilling={isStructure}
            />
            <MediaSectionBox
                projectId={projectId}
                ownerType="findingProfile"
                ownerId={isEdit && profile ? profile.id : null}
                canEdit={canEditMedia}
                location={{
                    findingNumber: ownFindingNumber,
                    area: watchedArea,
                    profileNumber: watchedProfileNumber
                }}
                // A baulicher Bestand is measured in Lagen, so the Schichten tool
                // would seed a list it does not have.
                strataTool={!isStructure}
                onSketchStrata={isStructure ? undefined : seedStrata}
                // Only where the Profil carries the Bodenmerkmale (a seeded row
                // would otherwise bring up the soil section by itself), and only
                // for a user who can save the result.
                onSketchPens={
                    !readOnly &&
                    !isStructure &&
                    ownFinding?.onlyVisibleInProfile
                        ? seedInclusions
                        : undefined
                }
                onNavigate={guardedNav.requestNavigate}
            />

            {isEdit && profile && (
                <FormSection anchorId="references">
                    <DescriptionReferencesSection
                        descriptionId={profile.id}
                        findingId={profile.findingId}
                        findingNumber={ownFindingNumber}
                        kind="profile"
                        projectId={projectId}
                        onNavigate={guardedNav.requestNavigate}
                        canEdit={canEditMedia}
                    />
                </FormSection>
            )}
        </RecordFormDialog>
    );
}
